import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { JobSearchPanelProvider } from "@/contexts/JobSearchPanelContext";
import { JobSearchPanel } from "@/components/JobSearchPanel";
import { JobContextProvider } from "@/contexts/JobContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { DashboardLayout } from "@/components/DashboardLayout";
import { PrivateRoute, PublicRoute } from "@/router/guards";
import { AdminRoute } from "@/components/AdminRoute";
import { TawkToWidget } from "@/components/TawkToWidget";
import { ChatWidget } from "@/components/chat/ChatWidget";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import VerifyMFA from "./pages/VerifyMFA";
import Onboarding from "./pages/Onboarding";
import Pricing from "./pages/Pricing";
import Resume from "./pages/Resume";
import ResumeCreationHub from "./pages/ResumeCreationHub";
import ResumeOptimizationResult from "./pages/ResumeOptimizationResult";
import ResumeLibrary from "./pages/ResumeLibrary";
import TailorToJob from "./pages/TailorToJob";
import Letters from "./pages/Letters";
import Documents from "./pages/Documents";
import Contact from "./pages/Contact";
import Account from "./pages/Account";
import AtsOptimization from "./pages/AtsOptimization";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import AdminDiagnostics from "./pages/AdminDiagnostics";
import AdminPerformance from "./pages/AdminPerformance";
import Analytics from "./pages/Analytics";
import JobAnalyzer from "./pages/JobAnalyzer";
import LinkedInOptimizer from "./pages/LinkedInOptimizer";
import ApplicationTracker from "./pages/ApplicationTracker";
import AtsScoreChecker from "./pages/AtsScoreChecker";
import JobSearch from "./pages/JobSearch";
import AIApplication from "./pages/AIApplication";
import AIInterviewPrep from "./pages/AIInterviewPrep";
import CompanyInsightsApp from "./pages/CompanyInsights";
import SmartApplyQueue from "./pages/SmartApplyQueue";
import InterviewPractice from "./pages/InterviewPractice";
import InterviewResults from "./pages/InterviewResults";
import InterviewHistory from "./pages/InterviewHistory";
import InterviewQuestions from "./pages/InterviewQuestions";
import AnswerLibrary from "./pages/AnswerLibrary";
import InterviewAnalytics from "./pages/InterviewAnalytics";
import GenerateFeatureImages from "./pages/GenerateFeatureImages";
import AdminAIMetrics from "./pages/AdminAIMetrics";
import AdminCacheStats from "./pages/AdminCacheStats";
import ResumeBuilder from "./pages/products/ResumeBuilder";
import CoverLetterGenerator from "./pages/products/CoverLetterGenerator";
import AutoApplyProduct from "./pages/products/AutoApply";
import JobSearchPage from "./pages/products/JobSearchPage";
import ApplicationTrackerPage from "./pages/products/ApplicationTrackerPage";
import CompanyInsights from "./pages/products/CompanyInsights";
import AnalyticsDashboardPage from "./pages/products/AnalyticsDashboardPage";
import InterviewPrepPage from "./pages/products/InterviewPrepPage";
import LinkedInOptimizerPage from "./pages/products/LinkedInOptimizerPage";
import FollowUpAutomation from "./pages/products/FollowUpAutomation";
import SalaryNegotiationMarketing from "./pages/products/SalaryNegotiation";
import SalaryNegotiation from "./pages/SalaryNegotiation";
import ATSScoreCheckerPage from "./pages/products/ATSScoreCheckerPage";
import ForOrganizations from "./pages/ForOrganizations";
import Universities from "./pages/organizations/Universities";
import CorporateOutplacement from "./pages/organizations/CorporateOutplacement";
import Government from "./pages/organizations/Government";
import NonProfits from "./pages/organizations/NonProfits";
import WorkforceDevelopment from "./pages/organizations/WorkforceDevelopment";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import Help from "./pages/Help";
import ConnectedAccounts from "./pages/ConnectedAccounts";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <JobContextProvider>
          <JobSearchPanelProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <JobSearchPanel />
              <ScrollToTop />
              <TawkToWidget />
              <ChatWidget />
            <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          
          {/* Auth routes - redirect to dashboard if already logged in */}
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-mfa" element={<VerifyMFA />} />
          </Route>
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/for-organizations" element={<ForOrganizations />} />
          <Route path="/organizations/universities" element={<Universities />} />
          <Route path="/organizations/corporate-outplacement" element={<CorporateOutplacement />} />
          <Route path="/organizations/government" element={<Government />} />
          <Route path="/organizations/non-profits" element={<NonProfits />} />
          <Route path="/organizations/workforce-development" element={<WorkforceDevelopment />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/ats-optimization" element={<AtsOptimization />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          
          {/* Product Pages */}
          <Route path="/resume" element={<ResumeBuilder />} />
          <Route path="/ats-score-checker" element={<ATSScoreCheckerPage />} />
          <Route path="/letters" element={<CoverLetterGenerator />} />
          <Route path="/ai-application" element={<AutoApplyProduct />} />
          <Route path="/job-search" element={<JobSearchPage />} />
          <Route path="/application-tracker" element={<ApplicationTrackerPage />} />
          <Route path="/job-analyzer" element={<CompanyInsights />} />
          <Route path="/analytics" element={<AnalyticsDashboardPage />} />
          <Route path="/ai-interview-prep" element={<InterviewPrepPage />} />
          <Route path="/linkedin-optimizer" element={<LinkedInOptimizerPage />} />
          <Route path="/follow-up-automation" element={<FollowUpAutomation />} />
          <Route path="/salary-negotiation" element={<SalaryNegotiationMarketing />} />
          
          {/* Protected App Routes - require authentication */}
          <Route element={<PrivateRoute />}>
            <Route path="/app" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route path="/app/onboarding" element={<Onboarding />} />
          <Route path="/app/resume/create" element={<ResumeCreationHub />} />
          <Route path="/app/resume/optimize" element={<ResumeOptimizationResult />} />
          <Route path="/app/resume/library" element={<ResumeLibrary />} />
          <Route path="/app/resume/tailor-to-job" element={<TailorToJob />} />
          <Route path="/app/resume" element={<Resume />} />
          <Route path="/app/letters" element={<Letters />} />
          <Route path="/app/documents" element={<Documents />} />
          <Route path="/app/account" element={<Account />} />
          <Route path="/app/account/connections" element={<ConnectedAccounts />} />
          <Route path="/app/analytics" element={<Analytics />} />
          <Route path="/app/job-analyzer" element={<JobAnalyzer />} />
          <Route path="/company-insights" element={<CompanyInsights />} />
          <Route path="/app/linkedin-optimizer" element={<LinkedInOptimizer />} />
          <Route path="/app/application-tracker" element={<ApplicationTracker />} />
          <Route path="/app/ats-score-checker" element={<AtsScoreChecker />} />
          <Route path="/app/ats-optimization" element={<Navigate to="/app/ats-score-checker" replace />} />
          <Route path="/app/company-insights" element={<CompanyInsightsApp />} />
          <Route path="/app/smart-apply/queue" element={<SmartApplyQueue />} />
            <Route path="/app/job-search" element={<JobSearch />} />
            <Route path="/app/ai-application" element={<AIApplication />} />
            <Route path="/app/ai-interview-prep" element={<AIInterviewPrep />} />
            <Route path="/app/ai-interview-prep/practice/:mode" element={<InterviewPractice />} />
            <Route path="/app/ai-interview-prep/results/:sessionId" element={<InterviewResults />} />
            <Route path="/app/ai-interview-prep/history" element={<InterviewHistory />} />
            <Route path="/app/ai-interview-prep/questions" element={<InterviewQuestions />} />
            <Route path="/app/ai-interview-prep/library" element={<AnswerLibrary />} />
            <Route path="/app/ai-interview-prep/analytics" element={<InterviewAnalytics />} />
          <Route path="/app/salary-negotiation" element={<SalaryNegotiation />} />
          </Route>

          {/* Admin Routes - Protected with AdminRoute */}
          <Route element={<AdminRoute><PrivateRoute /></AdminRoute>}>
            <Route path="/app/admin" element={<Admin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/diagnostics" element={<DashboardLayout><AdminDiagnostics /></DashboardLayout>} />
            <Route path="/admin/generate-images" element={<DashboardLayout><GenerateFeatureImages /></DashboardLayout>} />
            <Route path="/admin/ai-metrics" element={<DashboardLayout><AdminAIMetrics /></DashboardLayout>} />
            <Route path="/admin/performance" element={<DashboardLayout><AdminPerformance /></DashboardLayout>} />
            <Route path="/admin/cache" element={<DashboardLayout><AdminCacheStats /></DashboardLayout>} />
          </Route>
          
          <Route path="/help" element={<Help />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
          </JobSearchPanelProvider>
        </JobContextProvider>
      </TooltipProvider>
    </QueryClientProvider>
    <SpeedInsights />
  </HelmetProvider>
);

export default App;
